import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  Checkbox,
  Select,
  MenuItem,
  IconButton,
  Button,
  Dialog,
  makeStyles,
  CircularProgress,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import DeleteIcon from "@material-ui/icons/Delete";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import GetAppIcon from "@material-ui/icons/GetApp";
import InputBase from "@material-ui/core/InputBase";
import Autocomplete from "@material-ui/lab/Autocomplete";
import axios from "axios";
import * as XLSX from "xlsx";
import { appUrl } from "../../../../../utils/config";
import ConfigureIDocTriggerPointDialog from "./ConfigureIDocTriggerPointDialog";

// Derives a short prefix (e.g. "Purchase Orders" -> "PO") for the "Add New Message"
// naming convention, since /fetchAllModules does not return one.
const getModulePrefix = (moduleName = "") =>
  moduleName
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .toUpperCase() || "MSG";

const FIELD_TYPE_OPTIONS = ["Chain of Custody", "First Choice", "Second Choice"];
const DATA_ELEMENT_OPTIONS = [
  "Sales order number",
  "PO Number",
  "PO Item Number",
  "Delivery Quantity",
  "UoM",
  "BMS Product Code",
  "Vendor ID",
  "Item text",
];
const INGESTION_OPTIONS = ["Mandatory", "Not Mandatory", "Do not read"];

const TRIGGER_POINT_TYPE_OPTIONS = ["PO Status", "Batch Status", "Changed Field", "Change Flag", "Day Wise", "Month Wise"];
const PO_STATUS_OPTIONS = ["Created", "Shared with CMO", "Under Confirmation", "Confirmed", "Changes Under Review", "Changed"];
const BATCH_STATUS_OPTIONS = ["Created", "In Process", "Completed", "Released", "Rejected", "On Hold"];
const CHANGED_FIELD_OPTIONS = ["Price per unit", "collaboratorTypeID"];
const CHANGE_FLAG_OPTIONS = ["acceptOfferFlag", "rejectOfferFlag", "changeInQty", "changeInDate", "shareWithCMOFlag"];
const CONDITIONS_FIELD_OPTIONS = ["Price per unit", "collaboratorTypeID"];
const CONDITIONS_OPERATOR_OPTIONS = ["Equal to", "Greater than", "Less than", "Greater than or equal to", "Less than or equal to", "Not equal to"];
const SELECT_DAYS_OPTIONS = ["All", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const SELECT_MONTHS_OPTIONS = ["All", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAY_OF_MONTH_OPTIONS = Array.from({ length: 30 }, (_, i) => String(i + 1));
const TIME_ZONE_OPTIONS = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "Asia/Kolkata",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Australia/Sydney",
];

const defaultTriggerConfig = () => ({
  triggerPoint: "",
  poStatuses: [],
  batchStatuses: [],
  changedFields: [],
  changeFlags: [],
  conditions: [],
  selectDays: [],
  time: "",
  timeZone: "",
  selectMonths: [],
  dayOfMonth: "",
});

let nextRowId = 1;
const newRowId = () => nextRowId++;

const blankVersion = () => ({
  rowId: newRowId(),
  buildNo: "",
  versionNo: "",
  description: "",
  xsdFileName: "",
  xsdFile: null,
  excelFileName: "",
  excelFile: null,
  excelValid: null,
  inbound: false,
  outbound: false,
  ingestionRows: [],
  triggerConfig: null,
  // Real messages get a collabrixMessageID from the (not-yet-integrated) messages-list API.
  // A brand-new/unsaved row has none yet, so its trigger point config can't be fetched.
  collabrixMessageID: null,
  isNew: true,
});

const validateExcelMapping = (file) => !file.name.toLowerCase().includes("invalid");

// Reads a File as a base64 string (without the "data:...;base64," prefix) for the
// /uploadNewMessage payload.
const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      resolve("");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      resolve(typeof result === "string" ? result.split(",")[1] || result : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Messages for the active module come from /fetchAllMessages/{collabrixModuleID};
// a module starts empty until that fetch resolves (see mapMessagesResponseToGroups).
const getSeedDataForModule = () => [];

// Maps the /fetchAllMessages/{collabrixModuleID} response into this component's
// internal { groupId, name, selected, versions: [...] } shape. Server rowIds are
// prefixed ("srv-") so they can never collide with locally-generated newRowId() ids
// for rows the user adds in this session (e.g. via "+" / "Add New Message").
const mapMessagesResponseToGroups = (messagesList = []) =>
  messagesList.map((msg) => {
    const buildsAndVersions = msg.buildsAndVersions || [];
    return {
      groupId: newRowId(),
      name: msg.messageCode,
      selected: buildsAndVersions.some((v) => v.activated),
      isNewGroup: false,
      versions: buildsAndVersions.map((v) => ({
        rowId: v.collabrixMessageID != null ? `srv-${v.collabrixMessageID}` : newRowId(),
        collabrixMessageID: v.collabrixMessageID,
        buildNo: v.buildNumber,
        versionNo: v.versionNumber,
        description: v.messageDescription,
        xsdFileName: "",
        xsdFile: null,
        excelFileName: "",
        excelFile: null,
        excelValid: null,
        inbound: !!v.inbound,
        outbound: !!v.outbound,
        activated: !!v.activated,
        markForDelete: false,
        xmlFormat: v.xmlFormat,
        excelFieldMappingFileID: v.excelFieldMappingFileID,
        ingestionRows: [],
        triggerConfig: null,
        isNew: false,
      })),
    };
  });

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  subTabsContainer: {
    display: "flex",
    gap: theme.spacing(3),
    borderBottom: "1px solid #ddd",
    marginBottom: theme.spacing(1),
  },
  subTab: {
    padding: "8px 4px",
    cursor: "pointer",
    fontSize: "14px",
    color: "#555",
    position: "relative",
  },
  activeSubTab: {
    color: "#1890FF",
    fontWeight: 600,
  },
  subTabIndicator: {
    position: "absolute",
    bottom: "-1px",
    left: 0,
    right: 0,
    height: "2px",
    backgroundColor: "#1890FF",
  },
  headerRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1.5),
  },
  tableWrapper: {
    overflowX: "auto",
  },
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    minWidth: "1100px",
  },
  bodyCell: {
    padding: "8px 12px",
    textAlign: "left",
    verticalAlign: "middle",
  },
  link: {
    color: "#1890FF",
    textDecoration: "underline",
    cursor: "pointer",
    fontSize: "13px",
  },
  linkDisabled: {
    color: "#aaa",
    textDecoration: "underline",
    fontSize: "13px",
  },
  textField: {
    "& input": {
      padding: "6px 8px",
      height: "24px",
    },
  },
  switchBase: {
    "&.Mui-checked": {
      color: "#007bff",
    },
    "&.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#007bff",
    },
  },
  iconBtn: {
    padding: "4px",
  },
  editIconOrange: {
    color: "#f26522",
  },
  editIconGray: {
    color: "#bbb",
  },
  addIconOrange: {
    color: "#f26522",
  },
  removeIconGray: {
    color: "#888",
  },
  saveButton: {
    backgroundColor: "#f26522",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 20px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#d4551a",
    },
  },
  resetButton: {
    border: "1px solid #000",
    color: "#000",
    fontWeight: 600,
    borderRadius: "20px",
    padding: "8px 20px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  addMessageButton: {
    marginTop: theme.spacing(1),
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    alignSelf: "flex-start",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  addIconCircle: {
    width: "22px",
    height: "22px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "14px",
    fontWeight: 600,
  },
  modalRoot: {
    fontFamily: theme.typography.fontFamily,
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderBottom: "1px solid #eee",
  },
  modalTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#000",
    marginRight: theme.spacing(1.5),
  },
  modalSubtitle: {
    fontSize: "13px",
    color: "#888",
  },
  modalCancelX: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    color: "#888",
    cursor: "pointer",
    fontSize: "13px",
  },
  modalBody: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
  },
  modalInfoBox: {
    display: "flex",
    gap: theme.spacing(5),
    backgroundColor: "#eef3fb",
    borderRadius: "6px",
    padding: theme.spacing(2, 3),
  },
  infoLabel: {
    fontSize: "11px",
    color: "#888",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },
  infoValue: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#000",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  existingTag: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#1890FF",
  },
  modalHint: {
    fontSize: "12px",
    color: "#888",
  },
  uploadRow: {
    display: "flex",
    gap: theme.spacing(3),
  },
  uploadBox: {
    flex: 1,
    border: "1px dashed #ddd",
    borderRadius: "6px",
    padding: theme.spacing(2),
  },
  uploadBoxHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  uploadLabel: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#000",
  },
  requiredStar: {
    color: "#e53935",
  },
  uploadSubtext: {
    fontSize: "12px",
    color: "#888",
    marginTop: "2px",
  },
  uploadBtn: {
    border: "1px solid #1890FF",
    color: "#1890FF",
    borderRadius: "4px",
    padding: "4px 16px",
    textTransform: "none",
    fontWeight: 600,
  },
  uploadSuccess: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: "#2e7d32",
    fontSize: "12px",
    marginTop: theme.spacing(1),
  },
  uploadError: {
    display: "flex",
    alignItems: "flex-start",
    gap: "6px",
    color: "#c62828",
    fontSize: "12px",
    marginTop: theme.spacing(1),
  },
  modalFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderTop: "1px solid #eee",
  },
  footerNote: {
    fontSize: "12px",
    color: "#888",
    maxWidth: "60%",
  },
  modalCancelBtn: {
    border: "1px solid #ccc",
    color: "#000",
    borderRadius: "4px",
    padding: "6px 20px",
    textTransform: "none",
    marginRight: theme.spacing(1.5),
  },
  modalSaveBtn: {
    backgroundColor: "#f26522",
    color: "#fff",
    borderRadius: "4px",
    padding: "6px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#d4551a",
    },
  },
  viewHeaderText: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#000",
  },
  viewSubheaderText: {
    fontSize: "12px",
    color: "#666",
    marginTop: "2px",
  },
  viewTableWrapper: {
    padding: theme.spacing(0, 3),
    maxHeight: "420px",
    overflow: "auto",
  },
  viewTable: {
    border: "1px solid #ddd",
    minWidth: "900px",
  },
  viewHeaderCell: {
    backgroundColor: "#f26522",
    color: "#fff",
    fontWeight: 600,
    fontSize: "12px",
    padding: "8px 10px",
    whiteSpace: "nowrap",
  },
  viewBodyCell: {
    fontSize: "12px",
    padding: "8px 10px",
    verticalAlign: "top",
  },
  searchBox: {
    border: "1px solid #ddd",
    borderRadius: "4px",
    padding: "4px 10px",
    fontSize: "13px",
    width: "220px",
  },
  downloadBtn: {
    backgroundColor: "#1890FF",
    color: "#fff",
    borderRadius: "4px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#146fce",
    },
  },
  // Parameters ("Click to view") modal — dark navy title/subtitle bars + striped table,
  // distinct from the orange-header viewTable/viewHeaderCell styling the (currently
  // disabled) Ingestion popup below still uses.
  paramsTopBar: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 3),
  },
  paramsTableWrapper: {
    maxHeight: "420px",
    overflow: "auto",
    "& tbody tr:nth-child(odd)": {
      backgroundColor: "#eaf1f8",
    },
    "& tbody tr:nth-child(even)": {
      backgroundColor: "#fff",
    },
  },
  paramsTable: {
    minWidth: "900px",
  },
  paramsHeaderCell: {
    backgroundColor: "#1f3a5f",
    color: "#fff",
    fontWeight: 700,
    fontSize: "12px",
    padding: "10px 12px",
    whiteSpace: "nowrap",
  },
  paramsBodyCell: {
    fontSize: "12px",
    padding: "8px 12px",
    verticalAlign: "top",
  },
  paramsCloseIcon: {
    display: "flex",
    alignItems: "center",
    color: "#555",
    cursor: "pointer",
  },
  ingestionHeaderCell: {
    backgroundColor: "#f5f5f5",
    color: "#333",
    fontWeight: 600,
    fontSize: "12px",
    padding: "8px 10px",
    whiteSpace: "nowrap",
  },
  outlinedAddBtn: {
    border: "1px solid #f26522",
    color: "#f26522",
    borderRadius: "20px",
    padding: "6px 18px",
    textTransform: "none",
    fontWeight: 600,
    marginTop: theme.spacing(1),
    "&:hover": {
      backgroundColor: "#fdeee3",
    },
  },
  deleteIconRed: {
    color: "#e53935",
  },
  compactSelect: {
    minWidth: "130px",
    fontSize: "12px",
  },
}));

const ModuleMessagesTab = (props) => {
  const { featuresAccess, onMessagesCountChange } = props;
  const classes = useStyles();
  const canEdit = featuresAccess?.editSponsorNetworkDetails ?? false;

  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [activeModuleId, setActiveModuleId] = useState(null);
  const [messagesByModule, setMessagesByModule] = useState({});
  const [messagesLoading, setMessagesLoading] = useState(false);
  const fetchedMessageModuleIdsRef = useRef(new Set());
  // Snapshot of each module's data exactly as last fetched from the server, so
  // "Reset Selection" can revert to it instead of wiping the table to nothing.
  const pristineMessagesByModuleRef = useRef({});
  const [isUpdated, setIsUpdated] = useState(false);
  const [isSavingSelection, setIsSavingSelection] = useState(false);
  const [saveSelectionError, setSaveSelectionError] = useState(null);
  const [uploadModal, setUploadModal] = useState(null); // { groupId, rowId }
  const [isUploadingMessage, setIsUploadingMessage] = useState(false);
  const [uploadModalError, setUploadModalError] = useState(null);
  const [savingRowId, setSavingRowId] = useState(null);
  const [rowSaveErrors, setRowSaveErrors] = useState({}); // { [rowId]: message }
  const [viewModal, setViewModal] = useState(null); // { groupId, rowId }
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [fileLoadError, setFileLoadError] = useState(null);
  const [viewFileRows, setViewFileRows] = useState([]); // sheet_to_json(header:1) rows
  const [viewFileURL, setViewFileURL] = useState(null); // Blob URL for the Download button
  const [viewFileName, setViewFileName] = useState("");
  const [viewSearch, setViewSearch] = useState("");
  const [ingestionModal, setIngestionModal] = useState(null); // { groupId, rowId }
  const [ingestionDraft, setIngestionDraft] = useState([]);
  const [triggerModal, setTriggerModal] = useState(null); // { groupId, rowId }

  // Fetch the modules to show as secondary tabs. Only modules configured as seed
  // data at the ecosystem level (returned here) get a tab — e.g. today only
  // Purchase Orders (collabrixModuleID 1) comes back, so it is the only tab shown.
  useEffect(() => {
    const fetchAllModules = async () => {
      setModulesLoading(true);
      try {
        const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchAllModules`;
        const response = await axios.get(url);
        console.log("fetchAllModules response:", response);
        const list = response?.data?.modulesList || [];
        setModules(list);
        if (list.length > 0) {
          setActiveModuleId((prev) => (prev !== null && prev !== undefined ? prev : list[0].collabrixModuleID));
        }
      } catch (err) {
        console.error("Error fetching modules:", err);
      } finally {
        setModulesLoading(false);
      }
    };
    fetchAllModules();
  }, []);

  // Fetches (or re-fetches) messages for a module from the server. Used both by
  // the once-per-module effect below and to refresh a module's data on demand
  // (e.g. right after /uploadNewMessage succeeds, so the new row gets its real
  // server-assigned collabrixMessageID instead of staying a local-only stand-in).
  const fetchMessagesForModule = async (moduleId) => {
    setMessagesLoading(true);
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchAllMessages/${moduleId}`;
      const response = await axios.get(url);
      console.log(`fetchAllMessages response (collabrixModuleID=${moduleId}):`, response);
      const list = response?.data?.messagesList || [];
      const mapped = mapMessagesResponseToGroups(list);
      pristineMessagesByModuleRef.current[moduleId] = mapped;
      setMessagesByModule((prev) => ({
        ...prev,
        [moduleId]: mapped,
      }));
    } finally {
      setMessagesLoading(false);
    }
  };

  // Fetch messages for whichever module tab is selected, once per module.
  useEffect(() => {
    if (activeModuleId === null || activeModuleId === undefined) return;
    if (fetchedMessageModuleIdsRef.current.has(activeModuleId)) return;
    fetchedMessageModuleIdsRef.current.add(activeModuleId);

    fetchMessagesForModule(activeModuleId).catch((err) => {
      console.error("Error fetching messages for module", activeModuleId, err);
      // Allow a retry on failure instead of permanently silencing this module.
      fetchedMessageModuleIdsRef.current.delete(activeModuleId);
    });
  }, [activeModuleId]);

  const activeModuleLabel = modules.find((m) => m.collabrixModuleID === activeModuleId)?.moduleName || "";
  const groups = messagesByModule[activeModuleId] ?? getSeedDataForModule(activeModuleId);

  // Surfaces the active module's message count to the parent tab label
  // (e.g. "Module Messages (2)"), matching the other Sponsor Network tabs.
  useEffect(() => {
    onMessagesCountChange?.(groups.length);
  }, [groups.length, onMessagesCountChange]);

  const modalGroup = uploadModal ? groups.find((g) => g.groupId === uploadModal.groupId) : null;
  const modalVersion = modalGroup ? modalGroup.versions.find((v) => v.rowId === uploadModal.rowId) : null;

  const viewGroup = viewModal ? groups.find((g) => g.groupId === viewModal.groupId) : null;
  const viewVersion = viewGroup ? viewGroup.versions.find((v) => v.rowId === viewModal.rowId) : null;

  // viewFileRows comes from XLSX.utils.sheet_to_json(sheet, { header: 1 }) — an
  // array of row-arrays, first row is whatever header the uploaded file has.
  const viewFileHeader = viewFileRows[0] || [];
  const viewFileBodyRows = viewFileRows.slice(1).filter((row) => {
    const term = viewSearch.trim().toLowerCase();
    if (!term) return true;
    return row.some((cell) => String(cell).toLowerCase().includes(term));
  });

  const ingestionGroup = ingestionModal ? groups.find((g) => g.groupId === ingestionModal.groupId) : null;
  const ingestionVersion = ingestionGroup ? ingestionGroup.versions.find((v) => v.rowId === ingestionModal.rowId) : null;

  const triggerGroup = triggerModal ? groups.find((g) => g.groupId === triggerModal.groupId) : null;
  const triggerVersion = triggerGroup ? triggerGroup.versions.find((v) => v.rowId === triggerModal.rowId) : null;

  const updateGroups = (updaterFn) => {
    setMessagesByModule((prev) => ({
      ...prev,
      [activeModuleId]: updaterFn(prev[activeModuleId] ?? getSeedDataForModule(activeModuleId)),
    }));
    setIsUpdated(true);
  };

  const toggleGroupSelected = (groupId) => {
    updateGroups((list) =>
      list.map((g) => (g.groupId === groupId ? { ...g, selected: !g.selected } : g))
    );
  };

  const addNewMessage = () => {
    const prefix = getModulePrefix(activeModuleLabel);
    const nextIndex = groups.length + 1;
    updateGroups((list) => [
      ...list,
      {
        groupId: newRowId(),
        name: `${prefix}-${nextIndex}`,
        selected: true,
        isNewGroup: true,
        versions: [blankVersion()],
      },
    ]);
  };

  const deleteGroup = (groupId) => {
    updateGroups((list) => list.filter((g) => g.groupId !== groupId));
  };

  const addNewVersion = (groupId) => {
    updateGroups((list) =>
      list.map((g) =>
        g.groupId === groupId ? { ...g, versions: [...g.versions, blankVersion()] } : g
      )
    );
  };

  const updateVersionFields = (groupId, rowId, fields) => {
    updateGroups((list) =>
      list.map((g) =>
        g.groupId !== groupId
          ? g
          : {
              ...g,
              versions: g.versions.map((v) => (v.rowId === rowId ? { ...v, ...fields } : v)),
            }
      )
    );
  };

  const updateVersionField = (groupId, rowId, field, value) =>
    updateVersionFields(groupId, rowId, { [field]: value });

  const openUploadModal = (groupId, rowId) => {
    setUploadModalError(null);
    setUploadModal({ groupId, rowId });
  };
  const closeUploadModal = () => {
    setUploadModal(null);
    setUploadModalError(null);
  };

  const handleXsdSelect = (file) => {
    if (!file || !uploadModal) return;
    updateVersionFields(uploadModal.groupId, uploadModal.rowId, {
      xsdFileName: file.name,
      xsdFile: file,
    });
  };

  const handleExcelSelect = (file) => {
    if (!file || !uploadModal) return;
    updateVersionFields(uploadModal.groupId, uploadModal.rowId, {
      excelFileName: file.name,
      excelFile: file,
      excelValid: validateExcelMapping(file),
    });
  };

  // Shared by both save paths for a new row: the Upload Parameters modal's own
  // Save button, and the row-level Save icon in the Action column. Both end up
  // calling /uploadNewMessage with whatever xsd/excel files were picked via the
  // modal — the row-level button just skips having to reopen the modal.
  const performUploadNewMessage = async (group, version) => {
    const [xsdFileData, excelFileData] = await Promise.all([
      readFileAsBase64(version.xsdFile),
      readFileAsBase64(version.excelFile),
    ]);

    const payload = {
      xsdFileName: version.xsdFileName,
      xsdFileData,
      excelFieldMappingFileName: version.excelFileName,
      excelFileData,
      messageCode: group.name,
      buildNumber: version.buildNo,
      versionNumber: version.versionNo,
      messageDescription: version.description,
      collabrixModuleID: activeModuleId,
      inbound: version.inbound,
      outbound: version.outbound,
      activated: group.selected,
    };

    const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/uploadNewMessage`;
    const response = await axios.post(url, payload);
    console.log("uploadNewMessage response:", response);

    // Refresh from the server so this message gets its real, server-assigned
    // collabrixMessageID (the local temp row has none, so it can't participate
    // in Save Selection's /updateMessages call until this refetch replaces it).
    try {
      await fetchMessagesForModule(activeModuleId);
    } catch (refetchErr) {
      console.error("Error refreshing messages after upload:", refetchErr);
      // The upload itself succeeded — fall back to marking the row saved locally
      // so the UI doesn't look like the save failed.
      saveVersion(group.groupId, version.rowId);
    }
  };

  const saveUploadModal = async () => {
    if (!modalGroup || !modalVersion || !canSaveVersion(modalVersion)) {
      closeUploadModal();
      return;
    }

    setIsUploadingMessage(true);
    setUploadModalError(null);
    try {
      await performUploadNewMessage(modalGroup, modalVersion);
      closeUploadModal();
    } catch (err) {
      console.error("Error saving new message:", err);
      setUploadModalError("Something went wrong while saving. Please try again.");
    } finally {
      setIsUploadingMessage(false);
    }
  };

  // Row-level Save (Action column, for a row that already has files picked via
  // the Upload Parameters modal but wasn't finalized there).
  const handleRowLevelSave = async (groupId, rowId) => {
    const group = groups.find((g) => g.groupId === groupId);
    const version = group?.versions.find((v) => v.rowId === rowId);
    if (!group || !version || !canSaveVersion(version)) return;

    setSavingRowId(rowId);
    setRowSaveErrors((prev) => ({ ...prev, [rowId]: null }));
    try {
      await performUploadNewMessage(group, version);
    } catch (err) {
      console.error("Error saving new message:", err);
      setRowSaveErrors((prev) => ({ ...prev, [rowId]: "Something went wrong while saving. Please try again." }));
    } finally {
      setSavingRowId(null);
    }
  };

  // Revokes the previous preview Blob URL (if any) before replacing/clearing it,
  // so we don't leak object URLs across repeated "Click to view" opens.
  const releaseViewFileURL = () => {
    setViewFileURL((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const fetchDownloadCustomFile = async (excelFieldMappingFileID) => {
    setIsFileLoading(true);
    setFileLoadError(null);
    releaseViewFileURL();
    setViewFileRows([]);
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/downloadCustomFile`;
      const response = await axios.post(url, { schrockenCustomFileID: excelFieldMappingFileID });
      console.log(`downloadCustomFile response (schrockenCustomFileID=${excelFieldMappingFileID}):`, response);

      if (!response?.data?.status || !response.data.fileData) {
        setFileLoadError("No parameters file was returned for this build/version.");
        return;
      }

      const { fileData, fileName } = response.data;
      const byteArray = new Uint8Array(fileData.data);

      const blob = new Blob([byteArray], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      setViewFileURL(URL.createObjectURL(blob));
      setViewFileName(fileName || "parameters.xlsx");

      const workbook = XLSX.read(byteArray, { type: "array" });
      // The uploaded workbook typically has an "Overview" sheet plus the actual
      // field mapping data on its own sheet — find that one by name instead of
      // always taking the first sheet.
      const fieldMappingSheetName =
        workbook.SheetNames.find((name) => /field.?mapping/i.test(name)) || workbook.SheetNames[0];
      const sheet = workbook.Sheets[fieldMappingSheetName];
      setViewFileRows(XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" }));
    } catch (err) {
      console.error("Error downloading custom file", excelFieldMappingFileID, err);
      setFileLoadError("Something went wrong while loading this file. Please try again.");
    } finally {
      setIsFileLoading(false);
    }
  };

  const openViewModal = (groupId, rowId, excelFieldMappingFileID) => {
    setFileLoadError(null);
    setViewSearch("");
    setViewModal({ groupId, rowId });
    if (excelFieldMappingFileID != null) {
      fetchDownloadCustomFile(excelFieldMappingFileID);
    }
  };
  const closeViewModal = () => {
    setViewModal(null);
    releaseViewFileURL();
    setViewFileRows([]);
  };

  const setInboundOutbound = (groupId, rowId, field) => {
    updateGroups((list) =>
      list.map((g) =>
        g.groupId !== groupId
          ? g
          : {
              ...g,
              versions: g.versions.map((v) =>
                v.rowId === rowId
                  ? {
                      ...v,
                      inbound: field === "inbound" ? !v.inbound : false,
                      outbound: field === "outbound" ? !v.outbound : false,
                    }
                  : v
              ),
            }
      )
    );
  };

  const canSaveVersion = (v) =>
    v.buildNo.trim() &&
    v.versionNo.trim() &&
    v.description.trim() &&
    v.xsdFileName &&
    v.excelFileName &&
    v.excelValid === true &&
    (v.inbound || v.outbound);

  const saveVersion = (groupId, rowId) => {
    updateGroups((list) =>
      list.map((g) =>
        g.groupId !== groupId
          ? g
          : {
              ...g,
              isNewGroup: false,
              versions: g.versions.map((v) => (v.rowId === rowId ? { ...v, isNew: false } : v)),
            }
      )
    );
  };

  const cancelVersion = (groupId, rowId) => {
    updateGroups((list) =>
      list
        .map((g) =>
          g.groupId !== groupId ? g : { ...g, versions: g.versions.filter((v) => v.rowId !== rowId) }
        )
        .filter((g) => g.versions.length > 0)
    );
  };

  // Soft-deletes an existing (server-backed) build/version row: it's hidden from
  // the table immediately, but only actually removed once Save Selection persists
  // markForDelete via /updateMessages (Reset Selection un-hides it again).
  const deleteVersion = (groupId, rowId) => {
    updateGroups((list) =>
      list.map((g) =>
        g.groupId !== groupId
          ? g
          : {
              ...g,
              versions: g.versions.map((v) => (v.rowId === rowId ? { ...v, markForDelete: true } : v)),
            }
      )
    );
  };

  const openIngestionModal = (groupId, rowId) => {
    const group = groups.find((g) => g.groupId === groupId);
    const version = group?.versions.find((v) => v.rowId === rowId);
    setIngestionDraft((version?.ingestionRows || []).map((r) => ({ ...r })));
    setIngestionModal({ groupId, rowId });
  };
  const closeIngestionModal = () => {
    setIngestionModal(null);
    setIngestionDraft([]);
  };
  const addIngestionRow = () => {
    setIngestionDraft((prev) => [
      ...prev,
      { rowId: newRowId(), segment: "", conditionalElement: "", field: "", fieldType: "", dataElement: "", ingestion: "" },
    ]);
  };
  const updateIngestionRow = (rowId, field, value) => {
    setIngestionDraft((prev) =>
      prev.map((r) =>
        r.rowId === rowId
          ? {
              ...r,
              [field]: value,
              ingestion: field === "fieldType" && value === "Chain of Custody" ? "Mandatory" : r.ingestion,
            }
          : r
      )
    );
  };
  const deleteIngestionRow = (rowId) => {
    setIngestionDraft((prev) => prev.filter((r) => r.rowId !== rowId));
  };
  const canSaveIngestion = (rows) => rows.length > 0 && rows.every((r) => r.segment.trim() && r.field.trim());
  const saveIngestionModal = () => {
    if (ingestionModal) {
      updateVersionField(ingestionModal.groupId, ingestionModal.rowId, "ingestionRows", ingestionDraft);
    }
    closeIngestionModal();
  };

  const openTriggerModal = (groupId, rowId) => setTriggerModal({ groupId, rowId });
  const closeTriggerModal = () => setTriggerModal(null);
  const saveTriggerModal = (draft) => {
    if (triggerModal) {
      updateVersionField(triggerModal.groupId, triggerModal.rowId, "triggerConfig", draft);
    }
    closeTriggerModal();
  };



  // Only Selection (activated, applied to every non-deleted version in the
  // message), markForDelete, and Inbound/Outbound are editable per the
  // /updateMessages contract — Build No./Version No./Description are read-only
  // for existing rows and aren't sent. New (unsaved) rows go through
  // /uploadNewMessage instead, so they're excluded here.
  const handleSaveSelection = async () => {
    const messagesList = groups.flatMap((group) =>
      group.versions
        .filter((v) => !v.isNew && v.collabrixMessageID != null)
        .map((v) => ({
          collabrixMessageID: v.collabrixMessageID,
          activated: group.selected,
          markForDelete: !!v.markForDelete,
          inbound: v.inbound,
          outbound: v.outbound,
        }))
    );

    if (messagesList.length === 0) {
      setIsUpdated(false);
      return;
    }

    setIsSavingSelection(true);
    setSaveSelectionError(null);
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateMessages`;
      const response = await axios.post(url, { messagesList });
      console.log("updateMessages response:", response);

      // Refresh from the server so the table (and the pristine baseline Reset
      // Selection reverts to) reflects what was actually persisted.
      try {
        await fetchMessagesForModule(activeModuleId);
      } catch (refetchErr) {
        console.error("Error refreshing messages after save:", refetchErr);
        // The save itself succeeded — fall back to finalizing locally so the
        // UI doesn't look like the save failed.
        setMessagesByModule((prev) => {
          const cleaned = (prev[activeModuleId] || [])
            .map((g) => ({ ...g, versions: g.versions.filter((v) => !v.markForDelete) }))
            .filter((g) => g.versions.length > 0);
          pristineMessagesByModuleRef.current[activeModuleId] = cleaned;
          return { ...prev, [activeModuleId]: cleaned };
        });
      }
      setIsUpdated(false);
    } catch (err) {
      console.error("Error updating messages:", err);
      setSaveSelectionError("Something went wrong while saving. Please try again.");
    } finally {
      setIsSavingSelection(false);
    }
  };

  const handleResetSelection = () => {
    if (!isUpdated) return;
    // Revert only the active module back to its last-fetched-from-server state,
    // rather than wiping every module's data to empty.
    setMessagesByModule((prev) => ({
      ...prev,
      [activeModuleId]: pristineMessagesByModuleRef.current[activeModuleId] || [],
    }));
    setIsUpdated(false);
    setSaveSelectionError(null);
    closeIngestionModal();
    closeTriggerModal();
  };

  return (
    <div className={classes.container}>
      <div className={classes.subTabsContainer}>
        {modulesLoading && modules.length === 0 && (
          <div className={classes.subTab}>Loading modules…</div>
        )}
        {!modulesLoading && modules.length === 0 && (
          <div className={classes.subTab}>No modules configured</div>
        )}
        {modules.map((tab) => (
          <div
            key={tab.collabrixModuleID}
            className={`${classes.subTab} ${tab.collabrixModuleID === activeModuleId ? classes.activeSubTab : ""}`}
            onClick={() => setActiveModuleId(tab.collabrixModuleID)}
          >
            {tab.moduleName}
            {tab.collabrixModuleID === activeModuleId && <div className={classes.subTabIndicator} />}
          </div>
        ))}
      </div>

      <div className={classes.headerRow} style={{ flexDirection: "column", alignItems: "flex-end" }}>
        <div style={{ display: "flex", gap: 12 }}>
          <Button
            className={classes.saveButton}
            onClick={handleSaveSelection}
            disabled={!isUpdated || !canEdit || isSavingSelection}
            style={{ opacity: !isUpdated || !canEdit || isSavingSelection ? 0.7 : 1 }}
          >
            {isSavingSelection ? "Saving…" : "Save Selection"}
          </Button>
          <Button
            className={classes.resetButton}
            onClick={handleResetSelection}
            disabled={!isUpdated || !canEdit || isSavingSelection}
            style={{ opacity: !isUpdated || !canEdit || isSavingSelection ? 0.8 : 1 }}
          >
            Reset Selection
          </Button>
        </div>
        {saveSelectionError && (
          <div className={classes.uploadError} style={{ marginTop: 4 }}>
            <WarningIcon fontSize="small" /> {saveSelectionError}
          </div>
        )}
      </div>

      <div className={classes.tableWrapper}>
        <Table className={`${classes.table} default-table-styles`}>
          <TableHead>
            <TableRow>
              <TableCell>Selection</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Build No.</TableCell>
              <TableCell>Version No.</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Parameters</TableCell>
              <TableCell>Inbound</TableCell>
              <TableCell>Outbound</TableCell>
              <TableCell>IDoc Ingestion/Trigger Point</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {messagesLoading && groups.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            )}
            {!messagesLoading && groups.length === 0 && (
              <TableRow>
                <TableCell colSpan={10}>No messages configured for this module.</TableCell>
              </TableRow>
            )}
            {groups.map((group) => {
              const visibleVersions = group.versions.filter((v) => !v.markForDelete);
              return visibleVersions.map((v, vIndex) => {
                const isLast = vIndex === visibleVersions.length - 1;

                return (
                  <TableRow key={v.rowId}>
                    {vIndex === 0 && (
                      <TableCell className={classes.bodyCell} rowSpan={visibleVersions.length}>
                        <Switch
                          checked={group.selected}
                          onChange={() => toggleGroupSelected(group.groupId)}
                          classes={{ switchBase: classes.switchBase }}
                          disabled={!canEdit}
                        />
                      </TableCell>
                    )}
                    {vIndex === 0 && (
                      <TableCell className={classes.bodyCell} rowSpan={visibleVersions.length}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          {group.name}
                          {group.isNewGroup && canEdit && (
                            <IconButton
                              className={classes.iconBtn}
                              onClick={() => deleteGroup(group.groupId)}
                              aria-label="delete message"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </div>
                      </TableCell>
                    )}

                    <TableCell className={classes.bodyCell}>
                      {v.isNew ? (
                        <TextField
                          placeholder="Build No."
                          value={v.buildNo}
                          onChange={(e) => updateVersionField(group.groupId, v.rowId, "buildNo", e.target.value)}
                          className={classes.textField}
                          variant="outlined"
                        />
                      ) : (
                        v.buildNo
                      )}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {v.isNew ? (
                        <TextField
                          placeholder="Version No."
                          value={v.versionNo}
                          onChange={(e) => updateVersionField(group.groupId, v.rowId, "versionNo", e.target.value)}
                          className={classes.textField}
                          variant="outlined"
                        />
                      ) : (
                        v.versionNo
                      )}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {v.isNew ? (
                        <TextField
                          placeholder="Description"
                          value={v.description}
                          onChange={(e) => updateVersionField(group.groupId, v.rowId, "description", e.target.value)}
                          className={classes.textField}
                          variant="outlined"
                          fullWidth
                        />
                      ) : (
                        v.description
                      )}
                    </TableCell>

                    <TableCell className={classes.bodyCell}>
                      {v.isNew ? (
                        <span
                          className={canEdit ? classes.link : classes.linkDisabled}
                          onClick={() => canEdit && openUploadModal(group.groupId, v.rowId)}
                        >
                          Click to upload
                          {v.xsdFileName && v.excelFileName && v.excelValid === true && " ✓"}
                        </span>
                      ) : (
                        <span
                          className={classes.link}
                          onClick={() => openViewModal(group.groupId, v.rowId, v.excelFieldMappingFileID)}
                        >
                          Click to view
                        </span>
                      )}
                    </TableCell>

                    <TableCell className={classes.bodyCell}>
                      <Checkbox
                        checked={v.inbound}
                        onChange={() => setInboundOutbound(group.groupId, v.rowId, "inbound")}
                        disabled={!canEdit}
                        size="small"
                      />
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <Checkbox
                        checked={v.outbound}
                        onChange={() => setInboundOutbound(group.groupId, v.rowId, "outbound")}
                        disabled={!canEdit}
                        size="small"
                      />
                    </TableCell>

                    <TableCell className={classes.bodyCell}>
                      {!v.isNew && (
                        <IconButton
                          className={classes.iconBtn}
                          disabled={!canEdit || (!v.inbound && !v.outbound)}
                          onClick={() => {
                            if (v.outbound) openTriggerModal(group.groupId, v.rowId);
                            else if (v.inbound) openIngestionModal(group.groupId, v.rowId);
                          }}
                        >
                          <EditIcon
                            fontSize="small"
                            className={(v.inbound || v.outbound) && canEdit ? classes.editIconOrange : classes.editIconGray}
                          />
                        </IconButton>
                      )}
                    </TableCell>

                    <TableCell className={classes.bodyCell}>
                      {v.isNew ? (
                        <div>
                          <div style={{ display: "flex" }}>
                            <IconButton
                              className={classes.iconBtn}
                              disabled={savingRowId === v.rowId}
                              onClick={() => cancelVersion(group.groupId, v.rowId)}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              className={classes.iconBtn}
                              disabled={!canSaveVersion(v) || savingRowId === v.rowId}
                              onClick={() => handleRowLevelSave(group.groupId, v.rowId)}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </div>
                          {rowSaveErrors[v.rowId] && (
                            <div className={classes.uploadError} style={{ marginTop: 4 }}>
                              <WarningIcon fontSize="small" /> {rowSaveErrors[v.rowId]}
                            </div>
                          )}
                        </div>
                      ) : (
                        canEdit && (
                          <div style={{ display: "flex" }}>
                            {visibleVersions.length > 1 && (
                              <IconButton
                                className={classes.iconBtn}
                                onClick={() => deleteVersion(group.groupId, v.rowId)}
                                aria-label="delete build/version"
                              >
                                <RemoveCircleOutlineIcon fontSize="small" className={classes.removeIconGray} />
                              </IconButton>
                            )}
                            {isLast && (
                              <IconButton
                                className={classes.iconBtn}
                                onClick={() => addNewVersion(group.groupId)}
                                aria-label="add build/version"
                              >
                                <AddCircleOutlineIcon fontSize="small" className={classes.addIconOrange} />
                              </IconButton>
                            )}
                          </div>
                        )
                      )}
                    </TableCell>
                  </TableRow>
                );
              });
            })}
          </TableBody>
        </Table>
      </div>

      {canEdit && (
        <Button variant="outlined" onClick={addNewMessage} className={classes.addMessageButton}>
          <span className={classes.addIconCircle}>+</span>
          Add New Message
        </Button>
      )}

      <Dialog open={!!(uploadModal && modalGroup && modalVersion)} onClose={closeUploadModal} maxWidth="md" fullWidth>
        {modalGroup && modalVersion && (
          <div className={classes.modalRoot}>
            <div className={classes.modalHeader}>
              <div>
                <span className={classes.modalTitle}>Upload Parameters</span>
                <span className={classes.modalSubtitle}>{activeModuleLabel}</span>
              </div>
              <div className={classes.modalCancelX} onClick={closeUploadModal}>
                Cancel <CloseIcon fontSize="small" />
              </div>
            </div>

            <div className={classes.modalBody}>
              <div className={classes.modalInfoBox}>
                <div>
                  <div className={classes.infoLabel}>Message</div>
                  <div className={classes.infoValue}>
                    {modalGroup.name}
                    {!modalGroup.isNewGroup && <span className={classes.existingTag}>Existing</span>}
                  </div>
                </div>
                <div>
                  <div className={classes.infoLabel}>Build No.</div>
                  <div className={classes.infoValue}>{modalVersion.buildNo || "—"}</div>
                </div>
                <div>
                  <div className={classes.infoLabel}>Version No.</div>
                  <div className={classes.infoValue}>{modalVersion.versionNo || "—"}</div>
                </div>
                <div>
                  <div className={classes.infoLabel}>Description</div>
                  <div className={classes.infoValue}>{modalVersion.description || "—"}</div>
                </div>
              </div>

              <div className={classes.modalHint}>
                REQUIRED UPLOADS — details above came from the table and are read-only here
              </div>

              <div className={classes.uploadRow}>
                <div className={classes.uploadBox}>
                  <div className={classes.uploadBoxHeader}>
                    <div>
                      <span className={classes.uploadLabel}>
                        Upload XSD<span className={classes.requiredStar}>*</span>
                      </span>
                      <div className={classes.uploadSubtext}>IDoc structure definition (.xsd)</div>
                    </div>
                    <label>
                      <span className={classes.uploadBtn}>
                        Upload
                      </span>
                      <input
                        type="file"
                        accept=".xsd"
                        style={{ display: "none" }}
                        onChange={(e) => handleXsdSelect(e.target.files?.[0])}
                      />
                    </label>
                  </div>
                  {modalVersion.xsdFileName && (
                    <div className={classes.uploadSuccess}>
                      <CheckCircleIcon fontSize="small" /> {modalVersion.xsdFileName} uploaded
                    </div>
                  )}
                </div>

                <div className={classes.uploadBox}>
                  <div className={classes.uploadBoxHeader}>
                    <div>
                      <span className={classes.uploadLabel}>
                        Upload Excel mapping<span className={classes.requiredStar}>*</span>
                      </span>
                      <div className={classes.uploadSubtext}>Business segment mapping (.xlsx)</div>
                    </div>
                    <label>
                      <span className={classes.uploadBtn}>
                        Upload
                      </span>
                      <input
                        type="file"
                        accept=".xlsx"
                        style={{ display: "none" }}
                        onChange={(e) => handleExcelSelect(e.target.files?.[0])}
                      />
                    </label>
                  </div>
                  {modalVersion.excelFileName && modalVersion.excelValid === true && (
                    <div className={classes.uploadSuccess}>
                      <CheckCircleIcon fontSize="small" /> {modalVersion.excelFileName} uploaded
                    </div>
                  )}
                  {modalVersion.excelFileName && modalVersion.excelValid === false && (
                    <div className={classes.uploadError}>
                      <WarningIcon fontSize="small" />
                      Entered Mosymphony Field Name of Purchase Order is invalid. Please re upload file with
                      correct Mosymphony Field Name.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={classes.modalFooter}>
              <span className={classes.footerNote}>
                {uploadModalError ? (
                  <span className={classes.uploadError}>
                    <WarningIcon fontSize="small" /> {uploadModalError}
                  </span>
                ) : (
                  <>
                    On Save, the new line is added under the {modalGroup.isNewGroup ? "new" : "existing"} message{" "}
                    <strong>{modalGroup.name}</strong> in the Module Messages tab.
                  </>
                )}
              </span>
              <div>
                <Button className={classes.modalCancelBtn} onClick={closeUploadModal} disabled={isUploadingMessage}>
                  Cancel
                </Button>
                <Button
                  className={classes.modalSaveBtn}
                  disabled={
                    !(modalVersion.xsdFileName && modalVersion.excelFileName && modalVersion.excelValid === true) ||
                    isUploadingMessage
                  }
                  style={{
                    opacity:
                      modalVersion.xsdFileName &&
                      modalVersion.excelFileName &&
                      modalVersion.excelValid === true &&
                      !isUploadingMessage
                        ? 1
                        : 0.5,
                  }}
                  onClick={saveUploadModal}
                >
                  {isUploadingMessage ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Dialog>

      <Dialog open={!!(viewModal && viewGroup && viewVersion)} onClose={closeViewModal} maxWidth="lg" fullWidth>
        {viewGroup && viewVersion && (
          <div className={classes.modalRoot}>
            <div className={classes.paramsTopBar}>
              {viewFileRows.length > 0 && (
                <InputBase
                  placeholder="Search..."
                  value={viewSearch}
                  onChange={(e) => setViewSearch(e.target.value)}
                  className={classes.searchBox}
                />
              )}
              {viewFileURL && (
                <Button
                  className={classes.downloadBtn}
                  component="a"
                  href={viewFileURL}
                  download={viewFileName || "parameters.xlsx"}
                >
                  <GetAppIcon fontSize="small" style={{ marginRight: 6 }} />
                  Download
                </Button>
              )}
              <div className={classes.paramsCloseIcon} onClick={closeViewModal}>
                <CloseIcon fontSize="small" />
              </div>
            </div>
            <div className={classes.modalBody}>
              {isFileLoading && (
                <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
                  <CircularProgress size={28} />
                </div>
              )}
              {!isFileLoading && fileLoadError && (
                <div className={classes.uploadError}>
                  <WarningIcon fontSize="small" /> {fileLoadError}
                </div>
              )}
              {!isFileLoading && !fileLoadError && !viewVersion.excelFieldMappingFileID && (
                <div>No parameters file is available for this build/version.</div>
              )}
            </div>

            {!isFileLoading && !fileLoadError && viewFileRows.length > 0 && (
              <div className={classes.paramsTableWrapper}>
                <Table className={classes.paramsTable}>
                  <TableHead>
                    <TableRow>
                      {viewFileHeader.map((col, index) => (
                        <TableCell key={index} className={classes.paramsHeaderCell}>
                          {col}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {viewFileBodyRows.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {viewFileHeader.map((_, colIndex) => (
                          <TableCell key={colIndex} className={classes.paramsBodyCell}>
                            {row[colIndex]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </Dialog>

      {/* <Dialog
        open={!!(ingestionModal && ingestionGroup && ingestionVersion)}
        onClose={closeIngestionModal}
        maxWidth="lg"
        fullWidth
      >
        {ingestionGroup && ingestionVersion && (
          <div className={classes.modalRoot}>
            <div className={classes.modalHeader}>
              <span className={classes.modalTitle}>Configure IDoc Ingestion</span>
              <div className={classes.modalCancelX} onClick={closeIngestionModal}>
                Cancel <CloseIcon fontSize="small" />
              </div>
            </div>

            <div className={classes.modalBody}>
              <div className={classes.modalInfoBox}>
                <div>
                  <div className={classes.infoLabel}>Message</div>
                  <div className={classes.infoValue}>{ingestionGroup.name}</div>
                </div>
                <div>
                  <div className={classes.infoLabel}>Build-Version No.</div>
                  <div className={classes.infoValue}>
                    {ingestionVersion.buildNo}
                    {ingestionVersion.versionNo}
                  </div>
                </div>
                <div>
                  <div className={classes.infoLabel}>Description</div>
                  <div className={classes.infoValue}>{ingestionVersion.description}</div>
                </div>
              </div>

              <div className={classes.viewTableWrapper} style={{ padding: 0, maxHeight: "320px" }}>
                <Table className={classes.viewTable} style={{ minWidth: "1000px" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.ingestionHeaderCell}>
                        Segment<span className={classes.requiredStar}>*</span>
                      </TableCell>
                      <TableCell className={classes.ingestionHeaderCell}>Conditional Element</TableCell>
                      <TableCell className={classes.ingestionHeaderCell}>
                        Field (Technical)<span className={classes.requiredStar}>*</span>
                      </TableCell>
                      <TableCell className={classes.ingestionHeaderCell}>Field Type</TableCell>
                      <TableCell className={classes.ingestionHeaderCell}>Data Elements</TableCell>
                      <TableCell className={classes.ingestionHeaderCell}>Ingestion</TableCell>
                      <TableCell className={classes.ingestionHeaderCell}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ingestionDraft.map((row) => (
                      <TableRow key={row.rowId}>
                        <TableCell className={classes.viewBodyCell}>
                          <TextField
                            placeholder="Segment"
                            value={row.segment}
                            onChange={(e) => updateIngestionRow(row.rowId, "segment", e.target.value)}
                            className={classes.textField}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell className={classes.viewBodyCell}>
                          <TextField
                            placeholder="Conditional Element"
                            value={row.conditionalElement}
                            onChange={(e) => updateIngestionRow(row.rowId, "conditionalElement", e.target.value)}
                            className={classes.textField}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell className={classes.viewBodyCell}>
                          <TextField
                            placeholder="Field"
                            value={row.field}
                            onChange={(e) => updateIngestionRow(row.rowId, "field", e.target.value)}
                            className={classes.textField}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell className={classes.viewBodyCell}>
                          <Select
                            className={classes.compactSelect}
                            displayEmpty
                            value={row.fieldType}
                            onChange={(e) => updateIngestionRow(row.rowId, "fieldType", e.target.value)}
                          >
                            <MenuItem value="" disabled>
                              Select
                            </MenuItem>
                            {FIELD_TYPE_OPTIONS.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell className={classes.viewBodyCell}>
                          <Select
                            className={classes.compactSelect}
                            displayEmpty
                            value={row.dataElement}
                            onChange={(e) => updateIngestionRow(row.rowId, "dataElement", e.target.value)}
                          >
                            <MenuItem value="" disabled>
                              Select
                            </MenuItem>
                            {DATA_ELEMENT_OPTIONS.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell className={classes.viewBodyCell}>
                          <Select
                            className={classes.compactSelect}
                            displayEmpty
                            disabled={row.fieldType === "Chain of Custody"}
                            value={row.ingestion}
                            onChange={(e) => updateIngestionRow(row.rowId, "ingestion", e.target.value)}
                          >
                            <MenuItem value="" disabled>
                              Select
                            </MenuItem>
                            {INGESTION_OPTIONS.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell className={classes.viewBodyCell}>
                          <IconButton className={classes.iconBtn} onClick={() => deleteIngestionRow(row.rowId)}>
                            <DeleteIcon fontSize="small" className={classes.deleteIconRed} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Button className={classes.outlinedAddBtn} onClick={addIngestionRow}>
                + Add Row
              </Button>
            </div>

            <div className={classes.modalFooter} style={{ justifyContent: "flex-end" }}>
              <Button className={classes.modalCancelBtn} onClick={closeIngestionModal}>
                Cancel
              </Button>
              <Button
                className={classes.modalSaveBtn}
                disabled={!canSaveIngestion(ingestionDraft)}
                style={{ opacity: canSaveIngestion(ingestionDraft) ? 1 : 0.5 }}
                onClick={saveIngestionModal}
              >
                Save ›
              </Button>
            </div>
          </div>
        )}
      </Dialog> */}

      <ConfigureIDocTriggerPointDialog
        open={!!(triggerModal && triggerGroup && triggerVersion)}
        onClose={closeTriggerModal}
        onSave={saveTriggerModal}
        messageInfo={
          triggerGroup && triggerVersion
            ? {
                name: triggerGroup.name,
                buildNo: triggerVersion.buildNo,
                versionNo: triggerVersion.versionNo,
                description: triggerVersion.description,
              }
            : null
        }
        collabrixMessageID={triggerVersion?.collabrixMessageID}
      />
    </div>
  );
};

export default ModuleMessagesTab;
